import sanitize from 'sanitize-html';
import bootbox from 'bootbox';

import { applyBoxCSS, ButtonsOptions, confirmButtons } from './utils';

type PromptCallback = ((value: string) => void) | ((value: string[]) => void);

interface InputOption {
  text: string;
  value: string;
}

interface PromptOptions {
  title: string;
  inputType?:
    | 'text'
    | 'textarea'
    | 'email'
    | 'select'
    | 'checkbox'
    | 'date'
    | 'time'
    | 'number'
    | 'password'
    | 'radio'
    | 'range';
  inputOptions: InputOption[];
  buttons: ButtonsOptions;
  value?: string;
  callback: PromptCallback;
}

export async function promptAsync(options: Omit<PromptOptions, 'callback'>) {
  return new Promise((resolve) => {
    prompt({
      ...options,
      callback: (result: string | string[]) => resolve(result),
    });
  });
}

export function prompt(options: PromptOptions) {
  const box = bootbox.prompt({
    title: options.title,
    inputType: options.inputType,
    inputOptions: options.inputOptions,
    buttons: options.buttons ? confirmButtons(options.buttons) : undefined,
    // casting is done because ts definition expects string=>any, but library code can emit different values, based on inputType
    callback: options.callback as (value: string) => void,
    value: options.value,
  });

  applyBoxCSS(box);

  return box;
}

export function confirmContainerDeletion(
  title: string,
  callback: PromptCallback
) {
  const sanitizedTitle = sanitize(title);

  prompt({
    title: sanitizedTitle,
    inputType: 'checkbox',
    inputOptions: [
      {
        text: 'Автоматически удалять непостоянные тома<i></i>',
        value: '1',
      },
    ],
    buttons: {
      confirm: {
        label: 'Удалить',
        className: 'btn-danger',
      },
    },
    callback,
  });
}

export function selectRegistry(options: PromptOptions) {
  prompt(options);
}

export function confirmContainerRecreation(callback: PromptCallback) {
  const box = prompt({
    title: 'Вы уверены?',

    inputType: 'checkbox',
    inputOptions: [
      {
        text: 'Загрузить последнюю версию образа<i></i>',
        value: '1',
      },
    ],
    buttons: {
      confirm: {
        label: 'Пересоздать',
        className: 'btn-danger',
      },
    },
    callback,
  });

  const message = `Контейнер будет пересоздан: данные, не сохраненные в томах, будут потеряны. Текущий контейнер удаляется, новый создается с той же конфигурацией.`;

  customizeCheckboxPrompt(box, message);
}

export function confirmServiceForceUpdate(
  message: string,
  callback: PromptCallback
) {
  const sanitizedMessage = sanitize(message);

  const box = prompt({
    title: 'Вы уверены?',
    inputType: 'checkbox',
    inputOptions: [
      {
        text: 'Загрузить последнюю версию образа<i></i>',
        value: '1',
      },
    ],
    buttons: {
      confirm: {
        label: 'Обновить',
        className: 'btn-primary',
      },
    },
    callback,
  });

  customizeCheckboxPrompt(box, sanitizedMessage);
}

export function confirmKubeconfigSelection(
  options: InputOption[],
  expiryMessage: string,
  callback: PromptCallback
) {
  const message = sanitize(
    `Выберите окружения Kubernetes, которые нужно добавить в файл kubeconfig.</br>${expiryMessage}`
  );
  const box = prompt({
    title: 'Скачивание файла kubeconfig',
    inputType: 'checkbox',
    inputOptions: options,
    buttons: {
      confirm: {
        label: 'Скачать файл',
        className: 'btn-primary',
      },
    },
    callback,
  });

  customizeCheckboxPrompt(box, message, true, true);
}

function customizeCheckboxPrompt(
  box: JQuery<HTMLElement>,
  message: string,
  toggleCheckbox = false,
  showCheck = false
) {
  box.find('.bootbox-body').prepend(`<p>${message}</p>`);
  const checkbox = box.find('.bootbox-input-checkbox');
  checkbox.prop('checked', toggleCheckbox);

  if (showCheck) {
    checkbox.addClass('visible');
  }
}
